import { useEffect, useRef, useState } from "react";

// Set once we've kicked off the one-shot interactive re-auth for a foreign
// first-load token, so a redirect that lands back still-foreign doesn't loop.
// sessionStorage is the right scope: per-tab, and it survives the same-tab
// redirect (a ref would reset on the post-redirect page load).
const REAUTH_ATTEMPTED_KEY = "kinde_initial_reauth_attempted";

const safeGetItem = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSetItem = (key: string, value: string): void => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // sessionStorage unavailable (private mode, sandboxed). We lose loop
    // protection but the re-auth still proceeds.
  }
};

const safeRemoveItem = (key: string): void => {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
};

/**
 * Restores the v3 Kinde SDK's "reject a foreign-client token on load and
 * re-authenticate" behaviour, which the v5 upgrade dropped.
 *
 * v5's getToken() hands back whatever token the silent refresh produced without
 * validating which client minted it. On our shared `auth.inrange.dev` tenant a
 * cold-load silent refresh can return a token for a *different* client - e.g.
 * the inrange-agents app, which shares the auth-domain refresh cookie - a valid
 * token for the wrong `azp`/audience that both our token validator and BMA
 * reject, leaving the app stuck behind the "Session expired" modal.
 *
 * The first token we see after auth resolves is the no-state moment (the app is
 * still on its Loading screen, the user can't have typed anything yet), so if it
 * fails validation we re-authenticate with a full interactive `login()`. That's
 * client-scoped, so it mints *our* token off the shared SSO session and re-binds
 * the shared refresh cookie to our client. This mirrors v3 exactly: there, a
 * rejected token left the SDK unauthenticated and the app's login() gate fired.
 *
 * A wrong-`azp` token that appears *later* (a mid-session refresh) is left to
 * the normal relogin path (the admin "Session expired" modal), so in-progress
 * work isn't discarded by a redirect. The modal's "open in a new tab" recovery
 * then works because that new tab is itself a cold load and re-runs this check.
 *
 * Returns whether the initial-load token check has settled - true once a usable
 * own-client token is confirmed, or once we've given up after one redirect
 * attempt. Callers gate their render on it so the broken app, and the modal,
 * never flash before the redirect.
 */
export const useRejectForeignTokenOnInitialLoad = ({
  ready,
  getToken,
  validateToken,
  triggerReauth,
}: {
  // Auth has resolved (Kinde user present). The check waits for this.
  ready: boolean;
  getToken: () => Promise<string | undefined>;
  validateToken: (token: string) => boolean;
  // Performs a full-page interactive login() scoped to this app's client.
  triggerReauth: () => void;
}): boolean => {
  const checkedRef = useRef(false);
  const [initialAuthOk, setInitialAuthOk] = useState(false);

  useEffect(() => {
    if (!ready || checkedRef.current) return;
    checkedRef.current = true;
    void (async () => {
      const token = await getToken();
      if (token && validateToken(token)) {
        // Healthy own-client token. Clear the loop guard so a genuine future
        // expiry isn't mistaken for a redirect loop.
        safeRemoveItem(REAUTH_ATTEMPTED_KEY);
        setInitialAuthOk(true);
        return;
      }
      if (safeGetItem(REAUTH_ATTEMPTED_KEY)) {
        // We already redirected once and the token is still unusable. Stop, to
        // avoid a redirect loop - render and let the normal relogin path own it.
        setInitialAuthOk(true);
        return;
      }
      safeSetItem(REAUTH_ATTEMPTED_KEY, "1");
      triggerReauth();
      // Leave initialAuthOk false: the caller stays on its Loading screen while
      // the full-page redirect navigates away.
    })();
  }, [ready, getToken, validateToken, triggerReauth]);

  return initialAuthOk;
};
